
import { Vue, Component, Watch } from 'vue-property-decorator'
import axios from 'axios'
import { notification } from 'ant-design-vue'
import PageHeader from '@/components/base/PageHeader.vue'
import ActiveFilters from '@/components/listview/ActiveFilters.vue'
import Column, { FilterOption } from '@/types/column'
import TableSettings from '@/types/tableSettings'
import Pagination from '@/types/pagination'
import ls from '@/ls'
import { KAMFilterItem, KAMForm, KAMFormList } from '@/types/kam'
import { KAMFormStatusLabelColors } from '@/helpers/kamFormStates'
import { forms } from '@/views/kam/formRegistry'
import { can } from '@/globalFunctions'

@Component({
  components: { PageHeader, ActiveFilters }
})
export default class KAMForms extends Vue {
  localStorageName = 'KAMFormsTableSettings'
  downloading: Record<number, boolean> = {}

  can (permission: string) : boolean {
    return can(permission)
  }

  columns: Column[] = [
    { title: '#', key: 'id', dataIndex: 'id', sorter: true, sortDirections: ['ascend', 'descend'] },
    { title: 'Project', key: 'project.id', dataIndex: 'project', scopedSlots: { customRender: 'project' }, filters: [], sorter: false },
    { title: 'Formulier', key: 'form', dataIndex: 'form', scopedSlots: { customRender: 'form' }, filters: [], sorter: false },
    { title: 'Fase', key: 'phase', dataIndex: 'answers', scopedSlots: { customRender: 'phase' }, filters: [], sorter: false },
    { title: 'Eigenaar', key: 'owner', dataIndex: 'answers', scopedSlots: { customRender: 'owner' }, filters: [], sorter: false },
    { title: 'Bedrijf/contact', key: 'details', dataIndex: 'details', scopedSlots: { customRender: 'details' }, filters: [], sorter: false, width: 220 },
    { title: 'Projectleider', key: 'project_leader.id', dataIndex: 'project_leader', scopedSlots: { customRender: 'project_leader' }, filters: [], sorter: false, width: 180 },
    { title: 'Aangemaakt door', key: 'created_by_user.id', dataIndex: 'created_by_user', scopedSlots: { customRender: 'created_by_user' }, filters: [], sorter: false, width: 200 },
    { title: 'Gemeld op', key: 'created_at', dataIndex: 'created_at', scopedSlots: { customRender: 'created_at' }, sorter: true, sortDirections: ['ascend', 'descend'] },
    { title: 'Status', key: 'status', scopedSlots: { customRender: 'status' }, filters: [], sorter: false },
    { title: 'Actie\'s', key: 'action', scopedSlots: { customRender: 'action' }, width: 110 }
  ]

  tableSettings: TableSettings = ls.get(this.localStorageName) || {
    pagination: { current: 1, pageSize: 25 },
    showSizeChanger: true,
    pageSizeOptions: ['25', '50', '100'],
    activeFilters: null,
    sort: null,
    search: null,
    archived: false
  }

  mounted () : void {
    this.reload()
    this.onTableSettingsChange()
  }

  reload () : void {
    this.$store.dispatch('getKAMFormList', this.tableSettings)
  }

  archivedChanged (event: { target: { checked: boolean } }) : void {
    this.tableSettings.archived = event.target.checked
    if (this.tableSettings.activeFilters?.status) delete this.tableSettings.activeFilters.status
    this.reload()
  }

  handleTableChange (pagination:{ current:number, pageSize:number }, filters:{[key: string]: number[]|string[]}, sort:{ columnKey:string, order:string }) : void {
    if (filters && Object.keys(filters).length) {
      Object.keys(filters).forEach((key) => {
        if (!filters[key] || !filters[key].length) delete filters[key]
      })
    }
    this.tableSettings.pagination = pagination
    this.tableSettings.activeFilters = filters
    this.tableSettings.sort = sort.order ? sort : null
    this.reload()
  }

  handleSearchChange () : void {
    this.tableSettings.pagination.current = 1
    this.reload()
  }

  updateFilters (filters: {[key: string]: number[]|string[]|boolean[]}) : void {
    this.columns.forEach((column: Column) => {
      column.filteredValue = column.key in filters ? filters[column.key] : []
    })
    this.tableSettings.activeFilters = Object.assign({}, filters)
    this.reload()
  }

  onRowClick (item: KAMForm) : { on: { click: () => void }} {
    return {
      on: {
        click: () => this.navigateTo(item)
      }
    }
  }

  navigateTo (item: KAMForm) : void {
    const params: Record<string, string> = { kamFormId: item.id.toString() }
    if (item.form?.reference) params.formReference = item.form.reference
    this.$router.push({ name: 'kamFormEdit', params })
  }

  statusColor (status: number) : string {
    return KAMFormStatusLabelColors[status] || 'default'
  }

  phaseLabel (phase?: string) : string {
    const labels: Record<string, string> = {
      planvoorbereiding: 'Planvoorbereiding',
      ontwerp: 'Ontwerp',
      uitvoering: 'Uitvoering',
      evaluatie: 'Evaluatie',
      afgerond: 'Afgerond'
    }
    return phase ? labels[phase] || phase : '-'
  }

  canDownload (item: KAMForm) : boolean {
    return Number(item.status) > 1
  }

  filenamePart (value: string) : string {
    return value
      .replace(/[^a-z0-9_-]+/gi, '_')
      .replace(/^_+|_+$/g, '')
      .toLowerCase() || 'formulier'
  }

  async downloadKAMForm (item: KAMForm) : Promise<void> {
    this.$set(this.downloading, item.id, true)
    try {
      const response = await axios.get(process.env.VUE_APP_API_URL + '/kamForms/' + item.id + '/export', { responseType: 'arraybuffer' })
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `KAM_${this.filenamePart(item.form?.name || 'formulier')}_${item.project?.reference || 'project'}_${item.id}.pdf`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
      notification.success({ message: 'Download gestart.', description: 'De PDF export is gestart. Check je downloadmap.', duration: 3 })
    } catch {
      notification.error({ message: 'Download mislukt.', description: 'Het downloaden van het KAM-formulier is mislukt.', duration: 0 })
    } finally {
      this.$set(this.downloading, item.id, false)
    }
  }

  get itemList () : KAMFormList {
    return this.$store.getters.kamFormList
  }

  get loading () : boolean {
    return this.$store.getters.loadingKAMForm.list
  }

  get pagination () : Pagination {
    return {
      current: this.tableSettings.pagination.current,
      defaultPageSize: this.tableSettings.pagination.pageSize,
      pageSize: Number(this.itemList.meta.per_page),
      total: this.itemList.meta.total,
      showTotal: (total, range) => `KAM-formulier ${range[0]} t/m ${range[1]} van de ${total}`,
      showSizeChanger: this.tableSettings.showSizeChanger,
      pageSizeOptions: this.tableSettings.pageSizeOptions
    }
  }

  get filters () : Record<string, KAMFilterItem[]> {
    return {
      projects: this.itemList.filters?.projects || [],
      forms: this.itemList.filters?.forms || [],
      status: this.itemList.filters?.status || [],
      phase: this.itemList.filters?.phase || [],
      owner: this.itemList.filters?.owner || [],
      details: this.itemList.filters?.details || [],
      created_by_users: this.itemList.filters?.created_by_users || [],
      project_leaders: this.itemList.filters?.project_leaders || []
    }
  }

  mapTreeFilters (items: KAMFilterItem[]): FilterOption[] {
    return items.map((item) => ({
      text: item.name,
      value: item.id,
      children: item.children ? this.mapTreeFilters(item.children) : undefined
    }))
  }

  @Watch('filters', { immediate: true, deep: true })
  onFiltersChange () : void {
    const map: Record<string, string> = {
      'project.id': 'projects',
      form: 'forms',
      status: 'status',
      phase: 'phase',
      owner: 'owner',
      details: 'details',
      'project_leader.id': 'project_leaders',
      'created_by_user.id': 'created_by_users'
    }
    this.columns.forEach((column) => {
      const filterKey = map[column.key]
      if (column.key === 'form') {
        column.filters = forms.map((item) => ({ text: item.nameWithVersion, value: item.reference }))
      } else if (column.key === 'details' || column.key === 'created_by_user.id') {
        column.filters = this.mapTreeFilters(this.filters[filterKey] || [])
      } else if (filterKey && this.filters[filterKey]) {
        column.filters = this.filters[filterKey].map((item) => ({ text: item.name, value: item.id })) as FilterOption[]
      }
    })
  }

  @Watch('tableSettings', { deep: true })
  onTableSettingsChange () : void {
    ls.set(this.localStorageName, this.tableSettings)
    axios.post(process.env.VUE_APP_API_URL + '/users/admin/settings', {
      settingName: this.localStorageName,
      settingValue: this.tableSettings
    })

    this.columns.forEach((column: Column) => {
      column.filteredValue = this.tableSettings.activeFilters && column.key in this.tableSettings.activeFilters ? this.tableSettings.activeFilters[column.key] : null
      if (this.tableSettings.sort && column.key === this.tableSettings.sort.columnKey) {
        column.defaultSortOrder = this.tableSettings.sort.order
        column.sortOrder = this.tableSettings.sort.order
      } else {
        column.sortOrder = false
      }
    })
  }
}
